import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, apiError } from "../lib/api";
import { formatTime12 } from "../lib/data";
import { PatientCreditCard } from "./PatientCreditCard";
import { PatientEmailLog } from "./PatientEmailLog";
import { PatientFormsCard } from "./PatientFormsCard";

const input = "mt-1 w-full rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest";
const label = "text-xs font-semibold uppercase tracking-[0.15em] text-moss";
const btn = "rounded-md bg-forest px-4 py-2 text-sm font-semibold text-ivory transition-colors hover:bg-forest-dark disabled:opacity-50";

function Card({ title, children, testid }) {
  return (
    <section data-testid={testid} className="rounded-lg border border-linen bg-ivory p-5">
      <h3 className="font-serif text-xl font-medium text-forest">{title}</h3>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function PatientDetail({ email, onBack }) {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [purchase, setPurchase] = useState({ item_name: "", price: "", date: new Date().toISOString().slice(0, 10), notes: "" });
  const [note, setNote] = useState({ text: "", visible_to_patient: false });
  const [planId, setPlanId] = useState("");

  const load = useCallback(() => api.get(`/admin/patients/${encodeURIComponent(email)}`).then((r) => setData(r.data)), [email]);
  useEffect(() => { load(); api.get("/admin/membership-plans").then((r) => { setPlans(r.data); if (r.data[0]) setPlanId(r.data[0].id); }); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <p className="text-sm text-moss">Loading...</p>;
  const p = data.profile || { name: email, email };

  return (
    <div data-testid="patient-detail">
      <button data-testid="patient-back-button" onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-moss hover:text-forest"><ArrowLeft size={15} /> All patients</button>
      <h2 className="mt-4 font-serif text-3xl font-medium text-forest">{p.name}</h2>
      <p className="text-sm text-moss">{p.email}{p.phone ? ` · ${p.phone}` : ""}</p>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title="Add a purchase" testid="patient-add-purchase">
          <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/admin/patients/${encodeURIComponent(email)}/purchases`, { ...purchase, price: Number(purchase.price) }).then(() => setPurchase({ item_name: "", price: "", date: purchase.date, notes: "" })), "Purchase recorded"); }} className="space-y-3">
            <div><label className={label}>Treatment / product</label><input data-testid="purchase-item-input" required value={purchase.item_name} onChange={(e) => setPurchase({ ...purchase, item_name: e.target.value })} placeholder="Neurotoxin — 30 units" className={input} /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className={label}>Price ($)</label><input data-testid="purchase-price-input" type="number" min="0" required value={purchase.price} onChange={(e) => setPurchase({ ...purchase, price: e.target.value })} className={input} /></div>
              <div><label className={label}>Date</label><input data-testid="purchase-date-input" type="date" required value={purchase.date} onChange={(e) => setPurchase({ ...purchase, date: e.target.value })} className={input} /></div>
            </div>
            <div><label className={label}>Notes (optional)</label><input data-testid="purchase-notes-input" value={purchase.notes} onChange={(e) => setPurchase({ ...purchase, notes: e.target.value })} className={input} /></div>
            <button type="submit" data-testid="purchase-save-button" className={btn}>Add Purchase</button>
          </form>
          <ul className="mt-5 divide-y divide-linen text-sm">
            {data.purchases.map((x) => (
              <li key={x.id} className="flex items-center justify-between gap-3 py-2">
                <span className="text-forest">{x.item_name} <span className="text-xs text-moss">· {x.date}</span></span>
                <span className="flex items-center gap-3"><span className="font-semibold text-forest">${x.price}</span><button onClick={() => run(() => api.delete(`/admin/purchases/${x.id}`), "Removed")} className="text-moss hover:text-destructive" aria-label="Delete"><Trash2 size={14} /></button></span>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Progress notes" testid="patient-notes">
          <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/admin/patients/${encodeURIComponent(email)}/notes`, note).then(() => setNote({ text: "", visible_to_patient: false })), "Note added"); }} className="space-y-3">
            <textarea data-testid="note-text-input" required rows={3} value={note.text} onChange={(e) => setNote({ ...note, text: e.target.value })} placeholder="Great response to first tox session; plan 4 more units glabella next visit." className={input} />
            <label className="flex items-center gap-2 text-sm text-forest"><input data-testid="note-visible-input" type="checkbox" checked={note.visible_to_patient} onChange={(e) => setNote({ ...note, visible_to_patient: e.target.checked })} className="h-4 w-4 accent-forest" /> Share with patient in their portal <span className="text-xs text-moss">(unchecked = provider-only)</span></label>
            <button type="submit" data-testid="note-save-button" className={btn}>Add Note</button>
          </form>
          <ul className="mt-5 divide-y divide-linen text-sm">
            {data.notes.map((n) => (
              <li key={n.id} className="py-3">
                <p className="text-forest">{n.text}</p>
                <p className="mt-1 flex items-center gap-3 text-xs text-moss">{n.created_at.slice(0, 10)} · {n.visible_to_patient ? "shared with patient" : "provider-only"}<button onClick={() => run(() => api.delete(`/admin/notes/${n.id}`), "Removed")} className="text-moss hover:text-destructive" aria-label="Delete"><Trash2 size={13} /></button></p>
              </li>
            ))}
          </ul>
        </Card>

        <Card title="Memberships" testid="patient-memberships">
          <form onSubmit={(e) => { e.preventDefault(); run(() => api.post(`/admin/patients/${encodeURIComponent(email)}/memberships`, { plan_id: planId }), "Membership activated"); }} className="flex flex-wrap items-end gap-3">
            <div className="flex-1"><label className={label}>Plan</label>
              <select data-testid="membership-plan-select" value={planId} onChange={(e) => setPlanId(e.target.value)} className={input}>{plans.map((pl) => <option key={pl.id} value={pl.id}>{pl.name} — ${pl.price}/{pl.interval}</option>)}</select>
            </div>
            <button type="submit" data-testid="membership-add-button" disabled={!planId} className={btn}>Activate</button>
          </form>
          <ul className="mt-5 divide-y divide-linen text-sm">
            {data.memberships.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-3 py-2">
                <span className="text-forest">{m.plan_name} <span className="text-xs text-moss">· since {m.started_at.slice(0, 10)}</span></span>
                <select data-testid={`membership-status-${m.id}`} value={m.status} onChange={(e) => run(() => api.patch(`/admin/patient-memberships/${m.id}`, { status: e.target.value }), "Updated")} className="rounded-md border border-linen bg-ivory px-2 py-1 text-xs">
                  <option value="active">Active</option><option value="paused">Paused</option><option value="cancelled">Cancelled</option>
                </select>
              </li>
            ))}
            {data.memberships.length === 0 && <li className="py-2 text-xs text-moss">No memberships.</li>}
          </ul>
        </Card>

        <PatientCreditCard data={data} email={email} api={api} run={run} />

        <PatientFormsCard data={data} email={email} api={api} run={run} />

        <Card title="Visits & signed forms" testid="patient-history">
          <ul className="divide-y divide-linen text-sm">
            {data.bookings.map((b) => <li key={b.id} className="flex justify-between py-2"><span className="text-forest">{b.service_name}</span><span className="text-xs text-moss">{b.date} {formatTime12(b.time)} · {b.status}</span></li>)}
            {data.bookings.length === 0 && <li className="py-2 text-xs text-moss">No visits yet.</li>}
          </ul>
          <ul className="mt-4 divide-y divide-linen border-t border-linen text-sm">
            {data.forms.map((f) => <li key={f.id} className="flex justify-between py-2"><span className="text-forest">{f.form_name}</span><span className="text-xs text-moss">signed {f.submitted_at.slice(0, 10)}</span></li>)}
            {data.forms.length === 0 && <li className="py-2 text-xs text-moss">No forms signed yet.</li>}
          </ul>
        </Card>

        <PatientEmailLog emails={data.emails} />
      </div>
    </div>
  );
}

export default function PatientsTab() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const [patients, setPatients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => api.get("/admin/patients").then((r) => setPatients(r.data)), []);
  useEffect(() => { load(); }, [load, selected]);

  const removePatient = async (p) => {
    if (!window.confirm(`Delete ${p.name}'s patient account (${p.email})?\n\nThis removes their portal login, memberships and banked credit. Visit history, signed forms and notes are kept for your records.`)) return;
    try { await api.delete(`/admin/patients/${encodeURIComponent(p.email)}`); toast.success("Patient account deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (selected) return <PatientDetail email={selected} onBack={() => setSelected(null)} />;

  const list = patients.filter((p) => `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="patients-tab">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-serif text-3xl font-medium text-forest">Patients</h2>
          <p className="mt-1 text-sm text-moss">Everyone with a patient portal account. Record purchases, add notes, and manage memberships.</p>
        </div>
        <input data-testid="patients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="rounded-md border border-linen bg-ivory px-3 py-2 text-sm outline-none focus:border-forest sm:w-64" />
      </div>
      {list.length === 0 ? <p className="mt-8 text-sm text-moss">No patient accounts yet. Patients create one at /portal/login.</p> : (
        <div className="mt-6 overflow-x-auto rounded-lg border border-linen bg-ivory">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-linen text-xs uppercase tracking-[0.1em] text-moss"><tr><th className="px-4 py-3">Patient</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Visits</th><th className="px-4 py-3">Member</th><th className="px-4 py-3">Joined</th><th></th></tr></thead>
            <tbody className="divide-y divide-linen">
              {list.map((p) => (
                <tr key={p.email} data-testid={`patient-row-${p.email}`}>
                  <td className="px-4 py-3"><p className="font-medium text-forest">{p.name}</p><p className="text-xs text-moss">{p.email}</p></td>
                  <td className="px-4 py-3 text-moss">{p.phone || "—"}</td>
                  <td className="px-4 py-3 text-moss">{p.booking_count}</td>
                  <td className="px-4 py-3 text-moss">{p.has_membership ? "Yes" : "—"}</td>
                  <td className="px-4 py-3 text-moss">{(p.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right"><span className="flex items-center justify-end gap-2"><button data-testid={`patient-open-${p.email}`} onClick={() => setSelected(p.email)} className="rounded-md bg-forest px-3 py-1.5 text-xs font-semibold text-ivory hover:bg-forest-dark">Open</button>{isOwner && <button data-testid={`patient-delete-${p.email}`} onClick={() => removePatient(p)} className="rounded-md p-1.5 text-moss transition-colors hover:text-destructive" aria-label="Delete patient" title="Delete patient account"><Trash2 size={15} /></button>}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
